import type { ComponentProps, ReactNode } from 'react';
import type { ButtonProps } from '../../Button/Button';
import type { LinkButtonProps } from '../../LinkButton/LinkButton';

import { CloseButton } from '@headlessui/react';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from '../../Button/Button';
import LinkButton from '../../LinkButton/LinkButton';
import Tooltip from '../../Tooltip/Tooltip';

const colorClasses = {
  default:
    'text-default hover:text-default active:text-default focus:text-default',
  blue: 'text-blue hover:text-blue active:text-blue focus:text-blue',
  red: 'text-red-600 hover:text-red-600 dark:text-red',
  green: 'text-lime-700 hover:text-lime-700 dark:text-lime-500',
  orange: 'text-orange-500 hover:text-orange-700 dark:text-orange-700',
  purple: 'text-indigo-700 hover:text-indigo-700 dark:text-indigo-400',
  black:
    'text-slate-900 hover:text-slate-900 active:text-slate-900 focus:text-slate-900',
};

export const baseUIPopoverItemClasses =
  'h-9 xl:h-10 px-6 rounded-none w-full justify-end text-base 2xl:text-base' +
  ' hover:underline hover:bg-gradient-menu-active dark:hover:bg-gradient-dark-menu-active';

export type PopoverColor = keyof typeof colorClasses;

export interface PopoverItemBaseProps {
  /**
   * Popover button text color
   */
  color?: PopoverColor;
  /**
   * Button label
   */
  label?: ReactNode;
  /**
   * If button is disabled
   */
  disabled?: boolean;
  /**
   * Button tooltip text
   */
  tooltip?: string;
  /**
   * Whether to close the popover when the item is clicked.
   */
  closePopover?: boolean;
  /**
   * Additional popover button classes
   */
  additionalClasses?: string;
  /**
   * Additional popover button icon classes
   */
  additionalIconClasses?: string;
}

export type PopoverItemProps =
  | (PopoverItemBaseProps &
      Omit<LinkButtonProps, keyof PopoverItemBaseProps | 'as'> & {
        /**
         * Item link
         */
        link: string;
      })
  | (PopoverItemBaseProps &
      Omit<ButtonProps, keyof PopoverItemBaseProps | 'as'> & {
        /**
         * Item link
         */
        link?: never;
      });

const PopoverItem = ({
  iconImage,
  color = 'default',
  link,
  label = '',
  disabled = false,
  additionalClasses = '',
  additionalIconClasses = '',
  tooltip,
  closePopover,
  ...props
}: PopoverItemProps) => {
  const baseProps = useMemo(
    () => ({
      buttonColor: 'borderless' as const,
      buttonSize: 'base' as const,
      disabled,
      additionalClasses: twMerge(
        baseUIPopoverItemClasses,
        !disabled && 'cursor-pointer',
        colorClasses[color] || colorClasses.black,
        additionalClasses,
      ),
      ...(iconImage
        ? {
            iconImage,
            iconPosition: 'start' as const,
            additionalIconClasses: twMerge('mr-2', additionalIconClasses),
          }
        : {}),
    }),
    [iconImage, disabled, color, additionalClasses, additionalIconClasses],
  );

  const ItemComponent = link ? LinkButton : Button;
  const itemProps = {
    ...baseProps,
    ...props,
    link,
  } as ComponentProps<typeof ItemComponent>;

  return (
    <Tooltip
      tooltip={tooltip}
      tooltipPlacement="leftCenter"
      phoneTooltipPlacement="leftCenter"
    >
      {closePopover ? (
        <CloseButton as={ItemComponent} {...itemProps}>
          {label}
        </CloseButton>
      ) : (
        <ItemComponent {...itemProps}>{label}</ItemComponent>
      )}
    </Tooltip>
  );
};

export default PopoverItem;
